'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from "next-intl";
import { IoMdCloseCircle } from "react-icons/io";
import { useLocale } from 'next-intl';
import { FaFileDownload } from "react-icons/fa";
import MailChecker from "mailchecker";
import validator from "validator";
import moment from 'moment';
import { FaCircleNotch } from "react-icons/fa"; 
import { GrClose } from "react-icons/gr";



const HeroButtonDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [name, setName] = useState(''); // State for name input
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [error1, setError1] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null); // Ref for the dialog container
  const t = useTranslations('Hero');
  const l = useLocale();
 const now = moment();
  const te = useTranslations('Login');

 const isValidEmail = async (email: string): Promise<{ valid: boolean; message?: string }> => {
    // Step 1: Check if the email is empty or null
    if (!email || email.trim() === "") {
      return { valid: false, message: te('Email-is-required') };
    }

    // Step 2: Validate email format using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: te('Invalid-email-format') };
    }

    // Step 3: Validate email format using validator library
    if (!validator.isEmail(email)) {
      return { valid: false, message: te('Invalid-email-format') };
    }

    // Step 4: Check if the email is disposable using mailchecker
    if (!MailChecker.isValid(email)) {
      return { valid: false, message: te('Disposable-emails') };
    }

    // If all checks pass, the email is valid
    return { valid: true };
  };



 
    const handleSubscribe = async () => {
      if (!name) {
        setError(t('dialog-name-error')); // Add a translation key for name error
        return;
      }
  
      const emailValidation = await isValidEmail(email);
      if (!emailValidation.valid) {
        setError(emailValidation.message || te('Invalid-email'));
        return; // Exit the function if the email is invalid
      }
  
      setIsLoading(true);
      const language = l;
      const date = now.format('MMMM Do YYYY');
      const time = now.format('h:mm:ss a');
  
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_URL}offer/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Token "+process.env.NEXT_PUBLIC_TOKEN,
          },
          body: JSON.stringify({
            name,
            email,
            language,
            date,
            time,
          }),
        });
 
        // Log the raw response for debugging
        const rawResponse = await response.text();
  
        if (!response.ok) {
          // Handle HTTP errors
          const errorData = JSON.parse(rawResponse); // Try parsing the raw response
          setError(errorData.message || te('Subscription-failed'));
          return;
        }
  
        const data = JSON.parse(rawResponse); // Parse the raw response as JSON
        setIsSubscribed(true);
        setIsLoading(false);
        setError('');
        setEmail('');
      } catch (error) {
        console.error(te('Error-during-subscription'), error);
        setError(te('Network-error'));
      } finally {
        setIsLoading(false);
      }
    };
  

  // Close the dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setIsOpen(false); // Close the dialog
      }
    };

    // Add event listener when the dialog is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // Re-run effect when `isOpen` changes

  return (
    <>
      {/* Button to open the dialog */}
      <button
        onClick={() => setIsOpen(true)}
        className="mt-2 px-6 py-4 bg-secondary animate-bounce hover:bg-yel text-yel hover:text-secondary rounded-md flex items-center gap-2 uppercase"
      >
        {t('button-title')}
        <FaFileDownload />
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            ref={dialogRef} // Attach the ref to the dialog container
            className="bg-bl rounded-lg shadow-lg p-6 max-w-md w-full relative"
          >
            {/* Close Button */}
            {l === "ar" ? (
              <GrClose
                size={24}
                className="absolute top-2 left-2 text-yel hover:text-gray-700 cursor-pointer"
                onClick={() => setIsOpen(false)}
              />
            ) : (
              <GrClose
                size={24}
                className="absolute top-2 right-2 text-yel hover:text-gray-700 cursor-pointer"
                onClick={() => setIsOpen(false)}
              />
            )}

            {/* Scrollable Content */}
            <div className="max-h-[70vh] overflow-y-auto">
              {isSubscribed ? (
                <div>
                  <h2 className="text-xl font-semibold text-yel mb-4 uppercase">
                    {t('dialog-success-title')}
                  </h2>
                  <p className="text-white text-sm">
                    {t('dialog-success-t1')}{" "}
                    <strong>{t('dialog-success-t2')}</strong> {t('dialog-success-t3')}
                  </p>
                  <p className="text-white text-sm mt-2">
                    {t('dialog-success-t4')} <strong>{t('dialog-success-t5')}</strong> {t('dialog-success-t6')}{" "}
                    <strong>{t('dialog-success-t7')}</strong> {t('dialog-success-t8')}
                  </p>
                  <p className="text-white text-sm mt-4">
                    {t('dialog-success-t9')}
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-lg font-extrabold text-yel mb-4 mt-6 uppercase">
                    {t('dialog-title')}
                  </h2>
                  <p className="text-white mb-3 text-sm">
                    {t('dialog-t1')}
                  </p>
                  <p className="text-white mb-3 text-sm font-semibold underline">
                    {t('dialog-t2')}
                  </p>
                  <p className="text-white mb-3 text-sm">
                    {t('dialog-t3')}
                  </p>
                  <p className="text-white mb-3 text-sm">
                    {t('dialog-t4')}
                  </p>
                  <p className="font-semibold mb-3 text-sm text-yel">
                    {t('dialog-t5')}
                  </p>
                  <div className="flex flex-col space-y-4">
                    {/* Name Input */}
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('dialog-name-holder')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yel"
                    />
                    {error && <p className="text-bl text-sm">{error}</p>}
                    {/* Email Input */}
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('dialog-email-holder')}
                     className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yel"
                    />
                    {error1 && <p className="text-bl text-sm">{error1}</p>}
                  
                     {isLoading ? (
                                      <button
                                      aria-label=  {t('dialog-button')}
                                        onClick={handleSubscribe}
                                        className="w-full hover:bg-secondary hover:text-yel py-2 rounded-lg bg-yel font-medium ext-black transition-colors uppercase flex gap-3 justify-center items-center"
                                      >
                                       {t('dialog-button')}
                                        <FaCircleNotch className="animate-spin w-5 h-5"/>
                                      </button>
                                    ) : (
                                      <button
                                      aria-label=  {t('dialog-button')}
                                        onClick={handleSubscribe}
                                        className="w-full bg-secondary text-yel py-2 rounded-lg hover:bg-yel font-medium hover:text-black transition-colors uppercase"
                                      >
                                       {t('dialog-button')}
                                      </button>
                                    )}
                  
                  
                 
                    <p className="text-gray-50 text-xs">
                      {t('security-msg')}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroButtonDialog;